import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import PageShell from './PageShell';
import {
  CLASSIFICATION_AMENDES,
  VITESSE_2026,
  SANCTIONS_PENALES,
  SANCTIONS_ADMIN,
  TELEPHONE_CUMUL,
  PROBATOIRE,
  STAGE_48N,
  LOGIQUE_SANCTIONS,
  ACCROCHES,
  FIL_CONDUCTEUR,
} from '../data/infractionsSanctions2026';
import {
  AlertTriangle, Gauge, ShieldAlert, Smartphone, FileWarning, Scale, GraduationCap,
  Search, ArrowLeft, Filter, BookOpenCheck, AlertCircle, Sparkles,
} from 'lucide-react';

const TABS = [
  { id: 'logique',   label: 'Logique', icon: Sparkles },
  { id: 'amendes',   label: 'Amendes', icon: Scale },
  { id: 'vitesse',   label: 'Vitesse', icon: Gauge },
  { id: 'penales',   label: 'Pénales', icon: ShieldAlert },
  { id: 'admin',     label: 'Administratives', icon: FileWarning },
  { id: 'tel',       label: 'Téléphone', icon: Smartphone },
  { id: 'probatoire',label: 'Probatoire', icon: GraduationCap },
  { id: 'stage',     label: 'Stage 48N', icon: BookOpenCheck },
];

const RISQUE_COLOR = {
  majeur:   'bg-red-600/15 border-red-700/40 text-red-300',
  élevé:    'bg-amber-500/15 border-amber-600/40 text-amber-300',
  modéré:   'bg-sky-500/15 border-sky-700/40 text-sky-300',
  variable: 'bg-white/5 border-white/15 text-white/60',
};

function Pts({ v }) {
  if (v === '—' || v === 'non' || !v) return <span className="text-white/35">—</span>;
  const n = parseInt(v, 10);
  const color = n >= 6 ? 'bg-red-600 text-white' : n >= 4 ? 'bg-orange-500 text-white' : n >= 3 ? 'bg-amber-500 text-amber-950' : 'bg-sky-600 text-white';
  return <span className={`inline-flex items-center justify-center min-w-[34px] h-7 px-2 rounded-md text-[12px] font-bold ${color}`}>−{v}</span>;
}

function LogiquePanel() {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="panel-logique">
      {LOGIQUE_SANCTIONS.map((l, i) => (
        <div key={i} className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 hover:border-red-700/40 transition">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-2">Tranche {i + 1}</div>
          <div className="font-serif-editorial text-white text-[28px] leading-none">{l.tranche}</div>
          <div className="text-red-300 text-[13px] font-bold mt-1.5">{l.label}</div>
          <p className="text-white/65 text-[13px] mt-3 leading-relaxed">{l.description}</p>
        </div>
      ))}
    </div>
  );
}

function AmendesPanel() {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid="panel-amendes">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.04] text-white/50 uppercase tracking-wider font-mono-spec text-[10.5px]">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Nature</th>
              <th className="px-4 py-3 text-left font-bold">Amende max.</th>
              <th className="px-4 py-3 text-left font-bold">Minorée</th>
              <th className="px-4 py-3 text-left font-bold">Normale</th>
              <th className="px-4 py-3 text-left font-bold">Majorée</th>
              <th className="px-4 py-3 text-left font-bold">Lecture du risque</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {CLASSIFICATION_AMENDES.map((r, i) => (
              <tr key={i} className="hover:bg-white/[0.02] transition">
                <td className="px-4 py-3 text-white font-bold">{r.nature}</td>
                <td className="px-4 py-3 text-red-300 font-mono-spec">{r.max}</td>
                <td className="px-4 py-3 text-white/60 font-mono-spec">{r.minoree}</td>
                <td className="px-4 py-3 text-white/80 font-mono-spec">{r.normale}</td>
                <td className="px-4 py-3 text-white/80 font-mono-spec">{r.majoree}</td>
                <td className="px-4 py-3 text-white/65">{r.risque}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-3 bg-white/[0.02] border-t border-white/5 text-[11.5px] text-white/45 font-mono-spec">
        5ᵉ classe : tribunal de police. Délits : tribunal correctionnel. Certaines contraventions éligibles à la minorée.
      </div>
    </div>
  );
}

function VitessePanel() {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden" data-testid="panel-vitesse">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.04] text-white/50 uppercase tracking-wider font-mono-spec text-[10.5px]">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Dépassement</th>
              <th className="px-4 py-3 text-left font-bold">Amende</th>
              <th className="px-4 py-3 text-center font-bold">Points</th>
              <th className="px-4 py-3 text-left font-bold">Permis</th>
              <th className="px-4 py-3 text-left font-bold">Pourquoi c&apos;est risqué</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {VITESSE_2026.map((r, i) => (
              <tr key={i} className="hover:bg-white/[0.02] transition">
                <td className="px-4 py-3 text-white">{r.libelle}</td>
                <td className="px-4 py-3 text-red-300 font-mono-spec whitespace-nowrap">{r.amende}</td>
                <td className="px-4 py-3 text-center"><Pts v={r.points} /></td>
                <td className="px-4 py-3 text-white/65 font-mono-spec">{r.permis}</td>
                <td className="px-4 py-3 text-white/65">{r.risque}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PenalesPanel() {
  return (
    <div className="rounded-2xl border border-red-700/30 bg-[#0d0d0e] overflow-hidden" data-testid="panel-penales">
      <div className="px-4 py-3 bg-red-600/10 border-b border-red-700/30">
        <div className="text-[10.5px] uppercase tracking-wider text-red-300 font-bold font-mono-spec">Délit · procédure pénale · 6 pts + suspension/annulation</div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-white/[0.04] text-white/50 uppercase tracking-wider font-mono-spec text-[10.5px]">
            <tr>
              <th className="px-4 py-3 text-left font-bold">Libellé</th>
              <th className="px-4 py-3 text-left font-bold">Code route</th>
              <th className="px-4 py-3 text-left font-bold">Prison</th>
              <th className="px-4 py-3 text-left font-bold">Amende</th>
              <th className="px-4 py-3 text-left font-bold">Suspension</th>
              <th className="px-4 py-3 text-center font-bold">Pts</th>
              <th className="px-4 py-3 text-left font-bold">Risque</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {SANCTIONS_PENALES.map((r, i) => (
              <tr key={i} className="hover:bg-white/[0.02] transition">
                <td className="px-4 py-3 text-white font-bold">{r.libelle}</td>
                <td className="px-4 py-3 text-white/55 font-mono-spec text-[11.5px]">{r.code}</td>
                <td className="px-4 py-3 text-red-300 font-mono-spec whitespace-nowrap">{r.prison}</td>
                <td className="px-4 py-3 text-red-300 font-mono-spec whitespace-nowrap">{r.amende}</td>
                <td className="px-4 py-3 text-white/70 font-mono-spec">{r.suspension}</td>
                <td className="px-4 py-3 text-center"><Pts v={r.points} /></td>
                <td className="px-4 py-3 text-white/65">{r.risque}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdminPanel() {
  const [q, setQ] = useState('');
  const [risque, setRisque] = useState('all');
  const norm = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filtered = useMemo(() => SANCTIONS_ADMIN.filter((r) => {
    const okQ = !q || norm(r.libelle).includes(norm(q)) || norm(r.articles).includes(norm(q));
    const okR = risque === 'all' || r.risque === risque;
    return okQ && okR;
  }), [q, risque]);
  const niveaux = ['all', 'majeur', 'élevé', 'modéré', 'variable'];

  return (
    <div data-testid="panel-admin">
      {/* Recherche + filtres */}
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 mb-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une infraction, un article…"
            data-testid="admin-search"
            className="w-full h-11 pl-10 pr-4 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/35 text-[13px] focus:outline-none focus:border-red-500/50"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter size={13} className="text-white/40 mr-1" />
          {niveaux.map((n) => (
            <button
              key={n}
              onClick={() => setRisque(n)}
              data-testid={`filter-${n}`}
              className={`h-9 px-3 rounded-full border text-[11.5px] font-bold uppercase tracking-wider transition ${risque === n ? 'bg-red-600 border-red-600 text-white' : 'bg-white/5 border-white/10 text-white/55 hover:text-white hover:bg-white/10'}`}
            >
              {n === 'all' ? 'Tous' : n}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-white/[0.04] text-white/50 uppercase tracking-wider font-mono-spec text-[10.5px]">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Infraction</th>
                <th className="px-4 py-3 text-left font-bold">Articles</th>
                <th className="px-4 py-3 text-center font-bold">Pts</th>
                <th className="px-4 py-3 text-left font-bold">Suspension</th>
                <th className="px-4 py-3 text-left font-bold">Compléments</th>
                <th className="px-4 py-3 text-left font-bold">Risque</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((r, i) => (
                <tr key={i} className="hover:bg-white/[0.02] transition">
                  <td className="px-4 py-3 text-white">{r.libelle}</td>
                  <td className="px-4 py-3 text-white/55 font-mono-spec text-[11.5px]">{r.articles}</td>
                  <td className="px-4 py-3 text-center"><Pts v={r.pts} /></td>
                  <td className="px-4 py-3 text-white/70 font-mono-spec">{r.suspension}</td>
                  <td className="px-4 py-3 text-white/65">{r.comp || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[10.5px] font-bold uppercase tracking-wider ${RISQUE_COLOR[r.risque] || RISQUE_COLOR.variable}`}>{r.risque}</span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="6" className="px-4 py-8 text-center text-white/40">Aucune infraction ne correspond aux critères.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 bg-white/[0.02] border-t border-white/5 text-[11.5px] text-white/45 font-mono-spec">
          {filtered.length} / {SANCTIONS_ADMIN.length} infractions · Suspension pouvant être limitée à la conduite hors activité pro.
        </div>
      </div>
    </div>
  );
}

function TelephonePanel() {
  return (
    <div data-testid="panel-tel">
      <div className="rounded-2xl border border-orange-700/40 bg-gradient-to-br from-orange-950/30 via-[#0d0d0e] to-[#0d0d0e] p-5 mb-4">
        <div className="flex items-start gap-3">
          <Smartphone size={20} className="text-orange-400 shrink-0 mt-1" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-orange-400 font-bold font-mono-spec mb-1.5">Téléphone + une infraction de cette liste</div>
            <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Rétention immédiate puis suspension</h3>
            <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">Distraction + prise de risque = mesure immédiate possible. La 2ᵉ infraction transforme un simple téléphone tenu en main en risque majeur.</p>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-white/[0.04] text-white/50 uppercase tracking-wider font-mono-spec text-[10.5px]">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Infraction cumulée</th>
                <th className="px-4 py-3 text-left font-bold">Article</th>
                <th className="px-4 py-3 text-left font-bold">Pourquoi c&apos;est risqué</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {TELEPHONE_CUMUL.map((r, i) => (
                <tr key={i} className="hover:bg-white/[0.02] transition">
                  <td className="px-4 py-3 text-white">{r.libelle}</td>
                  <td className="px-4 py-3 text-white/55 font-mono-spec text-[11.5px]">{r.article}</td>
                  <td className="px-4 py-3 text-white/65">{r.risque}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProbatoirePanel() {
  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="panel-probatoire">
      {PROBATOIRE.map((p, i) => (
        <div key={i} className="rounded-2xl border border-amber-700/30 bg-amber-950/15 p-5">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-amber-300 font-bold font-mono-spec mb-1.5">{p.repere}</div>
          <div className="font-serif-editorial text-white text-[20px] leading-tight">{p.contenu}</div>
          <p className="text-white/65 text-[13px] mt-3 leading-relaxed italic">{p.pourquoi}</p>
        </div>
      ))}
    </div>
  );
}

function StagePanel() {
  return (
    <div data-testid="panel-stage">
      <div className="rounded-2xl border border-red-700/30 bg-gradient-to-br from-red-950/25 via-[#0d0d0e] to-[#0d0d0e] p-5 mb-4">
        <div className="flex items-start gap-3">
          <BookOpenCheck size={20} className="text-red-400 shrink-0 mt-1" />
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-1.5">Lettre 48N · stage de sensibilisation</div>
            <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Une seule infraction à ≥ 3 points = stage obligatoire</h3>
            <p className="text-white/65 text-[13.5px] mt-2 leading-relaxed">Délai : 4 mois à compter de la réception du courrier 48N. Le stage permet de récupérer jusqu&apos;à 4 points dans la limite du plafond probatoire.</p>
          </div>
        </div>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-white/[0.04] text-white/50 uppercase tracking-wider font-mono-spec text-[10.5px]">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Déclencheur</th>
                <th className="px-4 py-3 text-left font-bold">Règle</th>
                <th className="px-4 py-3 text-left font-bold">Effet</th>
                <th className="px-4 py-3 text-left font-bold">Lecture du risque</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {STAGE_48N.map((r, i) => (
                <tr key={i} className="hover:bg-white/[0.02] transition">
                  <td className="px-4 py-3 text-white font-bold">{r.declencheur}</td>
                  <td className="px-4 py-3 text-red-300">{r.regle}</td>
                  <td className="px-4 py-3 text-white/70">{r.effet}</td>
                  <td className="px-4 py-3 text-white/65 italic">{r.lecture}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const PANELS = {
  logique: LogiquePanel,
  amendes: AmendesPanel,
  vitesse: VitessePanel,
  penales: PenalesPanel,
  admin: AdminPanel,
  tel: TelephonePanel,
  probatoire: ProbatoirePanel,
  stage: StagePanel,
};

export default function InfractionsSanctionsPage() {
  const [tab, setTab] = useState('logique');
  const Panel = PANELS[tab];

  return (
    <PageShell>
      <section className="max-w-6xl mx-auto px-4 lg:px-6 mt-10 mb-16" data-testid="infractions-sanctions-page">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-[12px] text-white/50 font-mono-spec mb-6">
          <Link to="/" className="hover:text-white transition">codevoiture.fr</Link>
          <span className="text-white/25">/</span>
          <Link to="/cours-interactifs" className="hover:text-white transition">Cours interactifs</Link>
          <span className="text-white/25">/</span>
          <span className="text-white/70">Infractions & Sanctions 2026</span>
        </nav>

        {/* Hero */}
        <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-gradient-to-br from-[#15090a] via-[#0d0d0e] to-[#0d0d0e] p-5 sm:p-7 lg:p-9 mb-7 relative overflow-hidden">
          <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-red-600/12 blur-3xl" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-red-700/40 bg-red-600/10 mb-4">
              <AlertTriangle size={13} className="text-red-400" />
              <span className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold">Réglementation 2026 · MAJ janvier 2026</span>
            </div>
            <h1 className="font-serif-editorial text-white text-[34px] sm:text-[44px] lg:text-[54px] leading-[1.02] tracking-tight">
              Infractions <span className="text-red-500">&amp; Sanctions</span><br />
              tout le barème 2026
            </h1>
            <p className="text-white/70 text-[15px] mt-5 max-w-2xl leading-relaxed">
              Comprendre le lien <span className="text-red-400 font-semibold">risque ↔ sanction</span>. Amendes, vitesse, pénales, administratives, téléphone, probatoire, stage 48N : la base complète avec recherche et filtres.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-white/10 bg-white/5 text-white/70 text-[12.5px] font-mono-spec">
              <AlertCircle size={13} className="text-red-400" /> Fil conducteur : {FIL_CONDUCTEUR}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6" role="tablist">
          {TABS.map((t) => {
            const I = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                role="tab"
                aria-selected={active}
                data-testid={`tab-${t.id}`}
                className={`h-10 px-4 rounded-full font-bold text-[12.5px] inline-flex items-center gap-2 transition border ${active ? 'bg-red-600 text-white border-red-600 shadow-lg shadow-red-900/40' : 'bg-white/5 border-white/10 text-white/65 hover:bg-white/10 hover:text-white'}`}
              >
                <I size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Panel */}
        <div className="mb-8">
          <Panel />
        </div>

        {/* Accroches pédago */}
        <div className="rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 lg:p-6 mb-8">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec mb-3">À retenir</div>
          <div className="grid md:grid-cols-2 gap-3">
            {ACCROCHES.map((a, i) => (
              <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-white/[0.03] border border-white/5">
                <Sparkles size={13} className="text-red-400 shrink-0 mt-1" />
                <p className="text-white/80 text-[13.5px] leading-relaxed italic">{a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-6 border-t border-white/5">
          <Link to="/cours-interactifs" data-testid="back-to-tools" className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-[13px] font-semibold transition">
            <ArrowLeft size={14} /> Tous les outils
          </Link>
          <Link to="/cours/eleve/voiture" data-testid="cta-cours" className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13px] font-semibold shadow-lg shadow-red-900/40 transition">
            38 leçons REMC →
          </Link>
        </div>

        <div className="mt-6 text-[11.5px] text-white/40 font-mono-spec inline-flex items-center gap-2">
          <AlertCircle size={11} /> Barème actualisé janvier 2026 · codevoiture.fr · Susceptible d&apos;évolutions réglementaires.
        </div>
      </section>
    </PageShell>
  );
}
